import { Tooltip, TooltipProps } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenSize } from '@shared/hooks/use-screen';

type Props = {
  hasPlanAccess?: boolean;
  hasRoleAccess?: boolean;
  children: ReactNode;
  tooltip?: string; //  default tooltip when access is allowed
};

export function AccessControlledWrapper({ hasPlanAccess = true, hasRoleAccess = true, children, tooltip }: Props) {
  const canAccess = hasPlanAccess && hasRoleAccess;
  const { t } = useTranslation();
  const { isMobile } = useScreenSize();
  const trigger: TooltipProps['trigger'] = isMobile ? ['click'] : ['hover', 'focus'];

  if (canAccess) {
    return tooltip ? (
      <Tooltip title={tooltip} trigger={trigger} placement="bottomRight">
        {children}
      </Tooltip>
    ) : (
      <>{children}</>
    );
  }

  //  Priority: Plan > Role
  let tooltipTitle: string | undefined;

  if (!hasPlanAccess) {
    tooltipTitle = t('please_upgrade_here_to_higher_plan_to_access_this_feature');
  } else if (!hasRoleAccess) {
    tooltipTitle = t('this_feature_is_accessible_to_users_with_admin_role');
  }

  return (
    <Tooltip title={tooltipTitle} trigger={trigger} placement="bottomRight">
      {/* tabindex only on desktop: keyboard focus trigger; skipped on mobile since click trigger owns the toggle */}
      <div tabIndex={isMobile ? undefined : 0} aria-label={tooltipTitle} className="cursor-not-allowed">
        {/* pointer events  none added to children div for tooltip to not trigger on children */}
        <div className="pointer-events-none">{children}</div>
      </div>
    </Tooltip>
  );
}
